export interface ChipProps {
  label?: string;
  subLabel?: string;
  color?: Color;
  size?: Size;
  width?: Width;
  textPriority?: TextPriority;
  icon?: string;
  onRemove?: (value?: string[], included?: boolean) => void;
  isDisabled?: boolean;
  hasIndicator?: boolean;
  indicatorColor?: string;
  indicatorClassName?: string;
  dataTestId?: string;
  subLabelClassName?: string;
  wrapperClassName?: string;
  wrapperStyle?: React.CSSProperties;
  truncateLabel?: boolean;
}

export enum Color {
  PRIMARY = 'PRIMARY',
  GREY = 'GREY',
  DARK_GREY = 'DARK_GREY',
  LIGHT_PURPLE = 'LIGHT_PURPLE',
  PURPLE = 'PURPLE',
  PINK_RED = 'PINK_RED',
  YELLOW = 'YELLOW',
  LIME = 'LIME',
  PINK = 'PINK',
  ORANGE = 'ORANGE',
  SKY_BLUE = 'SKY_BLUE',
  RED = 'RED',
  GREEN = 'GREEN',
  DISABLED = 'DISABLED',
  NEUTRAL_LIGHT = 'NEUTRAL_LIGHT',
}

export enum Size {
  SMALL = 'SMALL',
  MEDIUM = 'MEDIUM',
  LARGE = 'LARGE',
}

export enum Width {
  MEDIUM = 'max-w-[200px]',
  SMALL = 'max-w-[160px]',
  FULL = '',
}

export enum TextPriority {
  LABEL = 'LABEL',
  EQUAL = 'EQUAL',
  SUBLABEL = 'SUBLABEL',
}

export const ChipTheme: Record<
  Color,
  {
    wrapperStyles: string;
    iconStyles: string;
    labelStyles: string;
    subLabelStyles: string;
    removeButtonStyles: string;
    indicatorStyles: string;
  }
> = {
  PRIMARY: {
    wrapperStyles: 'bg-primary-100',
    iconStyles: 'text-primary-600',
    labelStyles: 'text-primary-600',
    subLabelStyles: 'bg-primary-700 text-neutral-50',
    removeButtonStyles: 'text-primary-600',
    indicatorStyles: 'bg-primary-600',
  },
  GREY: {
    wrapperStyles: 'bg-neutral-950-opacity-10',
    iconStyles: 'text-neutral-900',
    labelStyles: 'text-neutral-800',
    subLabelStyles: 'bg-neutral-800 text-neutral-50',
    removeButtonStyles: 'text-neutral-900',
    indicatorStyles: 'bg-neutral-400',
  },
  DARK_GREY: {
    wrapperStyles: 'bg-neutral-800 hover:bg-neutral-800-opacity-10 active:bg-neutral-800-opacity-20',
    iconStyles: 'text-neutral-50 group-hover:text-neutral-50 group-active:text-neutral-50',
    labelStyles: 'text-neutral-50 group-hover:text-neutral-50 group-active:text-neutral-50',
    subLabelStyles: 'bg-neutral-800 text-neutral-50',
    removeButtonStyles: 'text-neutral-600 group-hover:text-neutral-800 group-active:text-neutral-800',
    indicatorStyles: 'bg-neutral-600',
  },
  PURPLE: {
    wrapperStyles: 'bg-purple-100',
    iconStyles: 'text-purple-950',
    labelStyles: 'text-purple-950',
    subLabelStyles: 'bg-purple-700 text-neutral-50',
    removeButtonStyles: 'text-purple-950',
    indicatorStyles: 'bg-purple-700',
  },
  LIGHT_PURPLE: {
    wrapperStyles: 'bg-purple-100 hover:bg-purple-100-opacity-5 active:bg-purple-100-opacity-10',
    iconStyles: 'text-purple-600',
    labelStyles: 'text-purple-600',
    subLabelStyles: 'bg-purple-600 text-neutral-50',
    removeButtonStyles: 'text-purple-600',
    indicatorStyles: 'bg-purple-600',
  },
  PINK_RED: {
    wrapperStyles: 'bg-pinkRed-100',
    iconStyles: 'text-pinkRed-900',
    labelStyles: 'text-pinkRed-900',
    subLabelStyles: 'bg-pinkRed-700 text-neutral-50',
    removeButtonStyles: 'text-pinkRed-900',
    indicatorStyles: 'bg-pinkRed-600',
  },
  YELLOW: {
    wrapperStyles: 'bg-yellow-100',
    iconStyles: 'text-yellow-950',
    labelStyles: 'text-yellow-950',
    subLabelStyles: 'bg-yellow-700 text-neutral-50',
    removeButtonStyles: 'text-yellow-950',
    indicatorStyles: 'bg-yellow-600',
  },
  LIME: {
    wrapperStyles: 'bg-lime-100',
    iconStyles: 'text-lime-950',
    labelStyles: 'text-lime-950',
    subLabelStyles: 'bg-lime-700 text-neutral-50',
    removeButtonStyles: 'text-lime-950',
    indicatorStyles: 'bg-lime-600',
  },
  PINK: {
    wrapperStyles: 'bg-pink-600-opacity-5 hover:bg-pink-600-opacity-10 active:bg-pink-600-opacity-20',
    iconStyles: 'text-pink-600',
    labelStyles: 'text-pink-600',
    subLabelStyles: 'bg-pink-700 text-neutral-50',
    removeButtonStyles: 'text-pink-600',
    indicatorStyles: 'bg-pink-600',
  },
  ORANGE: {
    wrapperStyles: 'bg-orange-100',
    iconStyles: 'text-orange-900',
    labelStyles: 'text-orange-900',
    subLabelStyles: 'bg-orange-700 text-neutral-50',
    removeButtonStyles: 'text-orange-900',
    indicatorStyles: 'bg-orange-600',
  },
  SKY_BLUE: {
    wrapperStyles: 'bg-sky-500-opacity-5 hover:bg-sky-500-opacity-10 active:bg-sky-500-opacity-20',
    iconStyles: 'text-sky-500',
    labelStyles: 'text-sky-500',
    subLabelStyles: 'bg-sky-600 text-neutral-50',
    removeButtonStyles: 'text-sky-500',
    indicatorStyles: 'bg-sky-500',
  },
  RED: {
    wrapperStyles: 'bg-pinkRed-100',
    iconStyles: 'text-pinkRed-900',
    labelStyles: 'text-pinkRed-900',
    subLabelStyles: 'bg-pinkRed-800 text-neutral-50',
    removeButtonStyles: 'text-pinkRed-900',
    indicatorStyles: 'bg-pinkRed-700',
  },
  GREEN: {
    wrapperStyles: 'bg-green-100',
    iconStyles: 'text-green-900',
    labelStyles: 'text-green-900',
    subLabelStyles: 'bg-green-700 text-neutral-50',
    removeButtonStyles: 'text-green-900',
    indicatorStyles: 'bg-green-700',
  },
  DISABLED: {
    wrapperStyles: 'bg-neutral-950-opacity-5',
    iconStyles: 'text-neutral-400',
    labelStyles: 'text-neutral-700',
    subLabelStyles: 'bg-neutral-300 text-neutral-400',
    removeButtonStyles: 'text-neutral-400 cursor-not-allowed',
    indicatorStyles: 'bg-neutral-400',
  },
  NEUTRAL_LIGHT: {
    wrapperStyles: 'bg-neutral-50',
    iconStyles: 'text-primary-600',
    labelStyles: 'text-primary-600',
    subLabelStyles: 'bg-primary-700 text-neutral-50',
    removeButtonStyles: 'text-primary-600',
    indicatorStyles: 'bg-primary-600',
  },
};

export const ChipSize: Record<
  Size,
  {
    wrapperSize: string;
    iconWithLabelSize: string;
    iconSize: string;
    labelSize: string;
    subLabelSize: string;
    removeButtonSize: string;
  }
> = {
  LARGE: {
    wrapperSize: 'rounded-md h-7',
    iconWithLabelSize: 'px-1.5',
    iconSize: 'w-4 h-4 stroke-1',
    labelSize: 'px-0.5 body-base',
    subLabelSize: 'px-2 rounded-md body-base',
    removeButtonSize: 'w-4 h-4 stroke-1',
  },
  SMALL: {
    wrapperSize: 'rounded-full h-5',
    iconWithLabelSize: 'px-[3px]',
    iconSize: 'w-3 h-3 stroke-1',
    labelSize: 'px-px body-sm',
    subLabelSize: 'px-[5px] rounded-full body-sm',
    removeButtonSize: 'w-3 h-3 stroke-1',
  },
  MEDIUM: {
    wrapperSize: 'rounded-full h-6',
    iconWithLabelSize: 'px-1.5',
    iconSize: 'w-4 h-4 stroke-1',
    labelSize: 'px-0.5 body-base',
    subLabelSize: 'px-2 rounded-full body-base',
    removeButtonSize: 'w-4 h-4 stroke-1',
  },
};
